import { StorageManager } from '@aws-amplify/ui-react-storage';
import MaterialTable, { MTableAction } from '@material-table/core';
import { DeleteOutline, InfoOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControlLabel, InputAdornment, Switch, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import phone from 'phone';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';

import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { getUrl } from 'aws-amplify/storage';
import { useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { PageAppBar } from '../../components/pageAppBar';
import { createDNC, deleteDNC } from '../../graphql/mutations';
import { dncByList, getDNCList } from '../../graphql/queries';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle, tabStyle } from '../../theme';
import { NotificationGroupSelect } from '../../components/notifications/NotificationGroupSelect';

export function DNC() {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const { id } = useParams();
	const [selDncList, setSelDncList] = useState({});
	const [dncs, setDncs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [delimiter, setDelimiter] = useState(';');
	const [skipInvalidNumbers, setSkipInvalidNumbers] = useState(false);
	const [currentTab, setCurrentTab] = useState(0);
	const [deleting, setDeleting] = useState(false);
	const [adding, setAdding] = useState(false);
	const [confirmDeleteSelection, setConfirmDeleteSelection] = useState(false);
	const [selectionToDelete, setSelectionToDelete] = useState([]);
	const [uploading, setUploading] = useState(false);
	const multiBoxRef = useRef(null);
	const numberTableRef = useRef();
	const [newNumberTrigger, setNewNumberTrigger] = useState(false);
	const [downloadEmail, setDownloadEmail] = useState('');
	const [confirmDownloadEmail, setConfirmDownloadEmail] = useState(false);
	const [notificationGroup, setNotificationGroup] = useState(null);

	const [searchQuery, setSearchQuery] = useState('');
	const [nextToken, setNextToken] = useState(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [pageTokens, setPageTokens] = useState({});

	useEffect(() => {
		async function getData() {
			searchDNC({ page: 0, pageSize: 10 });
			const result = await client.graphql({
				query: getDNCList,
				variables: {
					id,
					tenant: userContext.tenantId
				}
			});
			setSelDncList(result.data.getDNCList ? result.data.getDNCList : {});
			setDownloadEmail((await fetchUserAttributes()).email);
		}
		if (id && userContext.tenantId) {
			getData();
		}

	}, [id, userContext.tenantId]);

	function isNumberValid(number) {
		return phone(number, { validateMobilePrefix: false }).isValid;
	}

	function getMultiNumbers() {
		let separator = delimiter.trim();
		if (separator === '\\r\\n') {
			separator = '\n';
		}

		const numberStrings = multiBoxRef.current.split(separator);

		let hasInvalid = false;

		const numbers = [];
		const strings = [];

		for (let numberString of numberStrings) {
			numberString = numberString.trim();
			if (numberString) {
				const phoneNumber = phone(numberString, { validateMobilePrefix: false });
				if (!phoneNumber.isValid) {
					hasInvalid = true;
					numberString = `<span style="background-color: #E8A4BA; border-radius: 3px;">${numberString}</span>`;
				} else {
					numbers.push(phoneNumber.phoneNumber);
				}

				strings.push(numberString);
			}
		}

		multiBoxRef.current = strings.join(separator);

		if (hasInvalid && !skipInvalidNumbers) {
			return [false, []];
		}

		return [true, numbers];
	}

	function formatData() {
		let numbers = [];

		if (delimiter.trim() === '') {
			enqueueSnackbar("A delimiter is required", { variant: 'error' });
			return [false, []];
		}

		if (multiBoxRef.current.trim() === '') {
			enqueueSnackbar("Please provide at least one number", { variant: 'info' });
			return [false, []];
		}

		const result = getMultiNumbers();
		numbers = result[1];

		if (!result[0]) {
			enqueueSnackbar("Invalid number found.", { variant: 'error' });
			return [false, []];
		}


		if (numbers.length === 0) {
			enqueueSnackbar("No numbers to process", { variant: 'info' });
			return [false, []];
		}

		return [true, numbers];
	}

	async function handleAdd() {
		setAdding(true);
		const result = formatData();
		if (result[0]) {
			const resultResponse = await post({
				apiName: 'cdyxoutreach',
				path: '/dnc',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey
					},
					body: {
						tenant: userContext.tenantId,
						numbers: result[1],
						listId: selDncList.id
					}
				}
			}).response;
			const responseBody = await resultResponse.body.json();
			enqueueSnackbar(`Added ${responseBody.totalImported} numbers. ${responseBody.totalExistingNumbers} duplicate(s) were skipped.${responseBody.totalBadNumbers > 0 ? ` ${responseBody.totalBadNumbers} invalid numbers were found.` : ''}`, { variant: 'success' });
			searchDNC({ page: 0, pageSize: 10 });
		}
		setAdding(false);
		setCurrentTab(0);
	}

	async function handleRemove() {
		setDeleting(true);
		const result = formatData();
		if (result[0]) {
			const response = await unsafeDeleteListOfNumbers(result[1]);
			if (typeof (response) === 'object' && "numbers" in (response ?? {})) {
				enqueueSnackbar("Some numbers were unable to be deleted", { variant: 'warning' });
			}

			searchDNC({ page: 0, pageSize: 10 });
		}
		setDeleting(false);
	}

	async function unsafeDeleteListOfNumbers(numbers) {
		return await post({
			apiName: 'cdyxoutreach',
			path: '/dnc/delete',
			options: {
				headers: {
					Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
					'x-api-key': userContext.apiKey
				},
				body: {
					tenant: userContext.tenantId,
					numbers: numbers,
					listId: selDncList.id
				}
			},

		}).response;
	}

	async function handleUpload(path) {
		try {
			const urlObject = await getUrl({
				path,
				options: {
					level: 'private',
				},
			});

			const url = urlObject.url.toString();
			const session = await fetchAuthSession();

			await post({
				apiName: 'cdyxoutreach',
				path: '/dnc/import',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						tenant: userContext.tenantId,
						url,
						email: session.tokens.idToken.payload.email,
						listId: selDncList.id,
						notifyMethod: 'both',
						notificationGroup: notificationGroup ? notificationGroup : null,
						jwtToken: session.tokens.idToken.toString()
					}
				}
			}).response;

			const notifyMsg = notificationGroup
				? `File uploaded successfully. The "${notificationGroup.name}" group will be notified upon import completion.`
				: 'File uploaded successfully. You will receive a notification upon import completion.';
			enqueueSnackbar(notifyMsg, { variant: 'success' });
		} catch (err) {
			console.log(err);
			enqueueSnackbar("There was an error uploading and importing the file.", { variant: 'error' });
		}
		setUploading(false);
		setCurrentTab(0);
	}

	async function downloadDNCList() {
		try {
			setLoading(true);
			post({
				apiName: 'cdyxoutreach',
				path: '/dnc/export',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey
					},
					body: {
						email: downloadEmail,
						listId: selDncList.id
					}
				}
			})

			enqueueSnackbar("Your DNC List numbers will be sent to your email.", { variant: 'success' });

		} catch (error) {
			enqueueSnackbar("There was an error downloading the DNC List.", { variant: 'error' });
		}
		setLoading(false);
		setDownloadEmail('');
	}

	async function handleConfirmDeleteSelection(data) {
		setLoading(true);
		setConfirmDeleteSelection(false);
		setDeleting(true);
		await unsafeDeleteListOfNumbers(data.map(x => x.phoneNumber));
		const deletedHash = new Set(data.map(x => x.id));
		setDncs(dncs.filter(x => !deletedHash.has(x.id)));
		setDeleting(false);
		setLoading(false);
	}

	function handleAddNewNumber() {
		numberTableRef.current.state.showAddRow = true;
		setNewNumberTrigger(!newNumberTrigger);
	}

	const searchDNC = async (query) => {
		let currentPageTokens = {
			...pageTokens,
		};

		const params = {
			tenant: userContext.tenantId,
			dncList: id,
		}

		if (query.search) {
			setSearchQuery(query.search);
			window.sessionStorage.setItem('DNC search', query.search);
			params.phoneNumber = { 'beginsWith': query.search };
		}

		if (query.page > currentPage) {
			params.nextToken = nextToken;
			currentPageTokens[query.page] = nextToken;
		} else {
			if (query.search === searchQuery) {
				params.nextToken = currentPageTokens[query.page];
			} else {
				params.nextToken = null;
				currentPageTokens = {
					0: null,
				};
				setCurrentPage(0);
			}
		}

		if (query.pageSize) {
			params.limit = query.pageSize;
		}

		if (query.orderBy) {
			let sortField = `${query.orderBy.field}.keyword`;
			params.sort = [{ field: sortField, order: query.orderDirection }];
		}

		const dncSearch = await client.graphql({
			query: dncByList,
			variables: {
				...params,
				// filter: {
				// 	sortKey: {
				// 		eq: query.search
				// 	}
				// }

			}

		});

		if (dncSearch.data.dncByList !== null) {
			setNextToken(dncSearch.data.dncByList.nextToken);
			setCurrentPage(query.page);
			setPageTokens(currentPageTokens);

			return {
				data: dncSearch.data.dncByList.items,
				page: query.page
			}
		} else {
			return {
				data: [],
				page: query.page
			}
		}
	}

	return (
		<Box>
			<PageAppBar
				title={selDncList?.name ?? 'DNC List'}
				// description='Manage numbers in this DNC list'
				actionOneText={currentTab === 0 ? '+ Number' : ''}
				actionOneHandler={currentTab === 0 ? handleAddNewNumber : undefined}
				actionOneDisabled={userContext.securityOptions.suppression < 2}
				actionTwoText='Close'
				actionTwoHandler={() => window.history.back()}
			/>
			<Card variant='outlined' sx={cardStyle}>
				<CardContent>
					<Box>
						<Box display="flex" justifyContent="space-between" alignItems="center">
							<Tabs value={currentTab} onChange={(_, newTab) => setCurrentTab(newTab)} style={{ marginBottom: '10px' }}>
								<Tab sx={tabStyle} label="DNC List Numbers" id={0} />
								<Tab sx={tabStyle} label='Upload DNC Numbers' id={1} disabled={userContext.securityOptions.suppression < 2} />
								<Tab sx={tabStyle} label='Quick Add/Remove'
									// /Remove
									id={2} disabled={userContext.securityOptions.suppression < 2} />
							</Tabs>
							<Button sx={actionOneButtonStyle} onClick={() => setConfirmDownloadEmail(true)} disabled={loading || !selDncList}>
								Download DNC List
							</Button>
						</Box>
						<Divider />
						<Box>
							{currentTab === 2 &&
								<Box>
									<Box>
										<TextField
											label="Numbers"
											multiline
											rows={4}
											onChange={(e) => multiBoxRef.current = e.target.value}
											value={multiBoxRef.current}
											placeholder="Enter numbers here"
											sx={{ width: '100%', marginBottom: '10px' }}
											color="primary"
										/>

									</Box>
									<Box>
										<Box>
											<TextField
												label='Delimiter'
												variant='standard'
												color='primary'
												value={delimiter}
												onChange={e => setDelimiter(e.target.value)}
												error={delimiter.trim() === ''}
												helperText={delimiter.trim() === '' && <>A delimiter is required</>}
												InputProps={{
													endAdornment: (
														<InputAdornment position='end'>
															<Tooltip title='Type \r\n to designate a newline as the delimiter'>
																<InfoOutlined color='primary' />
															</Tooltip>
														</InputAdornment>
													)
												}} />
										</Box>
										<Box item>
											<FormControlLabel
												control={<Switch color='primary' checked={skipInvalidNumbers} onChange={() => setSkipInvalidNumbers(!skipInvalidNumbers)} />}
												label='Skip Invalid Numbers' />
										</Box>
									</Box>
									<Box display="flex">
										<Box >
											<Button sx={actionOneButtonStyle} onClick={handleAdd} disabled={adding || deleting || loading || !selDncList}>Add</Button>
										</Box>
										<Box item>
											<Button sx={actionTwoButtonStyle} onClick={handleRemove} disabled={adding || deleting || loading || !selDncList}>Remove</Button>
										</Box>
									</Box>
								</Box>}
							{currentTab === 1 &&
								<Box>
									<Box display='flex' gap={1} alignItems='center'>
										<InfoOutlined color='primary' />
										<Typography variant='body1'>Any column headers containing the word <i>phone</i> will be added</Typography>
									</Box>
									<Box display='flex' gap={1} alignItems='center' sx={{ my: 1 }}>
										<NotificationGroupSelect
											value={notificationGroup}
											onChange={(group) => setNotificationGroup(group)}
										/>
									</Box>
									<Box>
										<StorageManager
											displayText={{
												dropFilesText: 'Drop file here or'
											}}
											acceptedFileTypes={['.csv']}
											path={({ identityId }) => `private/${identityId}/`}
											maxFileCount={1}
											autoUpload={false}
											onUploadSuccess={(file) => {
												handleUpload(file.key);
											}

											}
										/>
									</Box>
								</Box>}
							{currentTab === 0 &&
								<Box>
									<Box item container direction="row" spacing={2}>
										<Box item style={{ flexGrow: 1, height: '100%' }}>
											<MaterialTable
												tableRef={numberTableRef}
												title=""
												isLoading={loading}
												columns={[
													{ title: 'Number', field: 'phoneNumber', validate: rowData => isNumberValid(rowData.phoneNumber) && (selDncList !== null) },
													{ title: 'Created', field: 'createdAt', render: rowData => rowData?.createdAt ? (new Date(rowData.createdAt)).toLocaleString() : (new Date()).toLocaleString(), editable: 'never' }
												]}
												data={searchDNC}
												onOrderCollectionChange={
													(o) => console.log(o)
												}
												options={{
													selection: true,
													showSelectAllCheckbox: true,
													actionsColumnIndex: -1,
													pageSize: 10,
													padding: 'dense',
													addRowPosition: 'first',
													searchFieldStyle: {
														marginBottom: '16px',
														marginleft: '-28px',
													},
													headerStyle: {
														borderTop: '1px solid #e0e0e0',
														padding: '16px',
													},
													searchAutoFocus: true,
													searchFieldVariant: 'outlined',
													paginationType: 'stepped',
												}}
												components={{
													Action: props => {
														if (props.action.tooltip !== "Add") return <MTableAction {...props} />;
														else return null;
													},
													Container: (props) => <Box {...props} elevation={0} />,
												}}
												icons={{
													Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} color='primary' />),
												}}
												actions={[
													{
														disabled: !selDncList || userContext.securityOptions.suppression < 2,
														tooltip: 'Remove All Selected Numbers',
														icon: 'delete',
														onClick: async (_, data) => {
															setSelectionToDelete(data);
															setConfirmDeleteSelection(true);
														}
													}
												]}
												editable={{
													isEditable: selDncList === null,
													onRowAdd: newData => new Promise(async (resolve, reject) => {
														try {
															// Check number isn't already in list before attempting to add
															const existing = await client.graphql({
																query: dncByList,
																variables: {
																	tenant: userContext.tenantId,
																	dncList: selDncList.id,
																	phoneNumber: { eq: phone(newData.phoneNumber, { validateMobilePrefix: false }).phoneNumber }
																}
															});
															if (existing.data.dncByList.items.length > 0) {
																enqueueSnackbar("This number already exists in the list.", { variant: 'warning' });
																reject();
																return;
															}
															const result = await client.graphql({
																query: createDNC,
																variables: {
																	input: {
																		phoneNumber: phone(newData.phoneNumber, { validateMobilePrefix: false }).phoneNumber,
																		dncList: selDncList.id,
																		tenant: userContext.tenantId
																	}
																}
															})

															setDncs([...dncs, result.data.createDNC]);
															resolve();
														} catch (err) {
															console.log(err);
															reject();
														}
													}),
													onRowDelete: toDelete => new Promise(async (resolve, reject) => {
														try {
															await client.graphql({
																query: deleteDNC,
																variables: {
																	input: {
																		id: toDelete.id
																	}
																}
															});

															enqueueSnackbar("Number deleted", { variant: 'success' });
															dncs.splice(toDelete.id, 1);
															setDncs([...dncs]);
															resolve();
														} catch (err) {
															console.log(err);
															enqueueSnackbar("Unable to delete number", { variant: 'error' });
															reject();
														}
													})
												}}
											/>
										</Box>
									</Box>
								</Box>}
						</Box>
					</Box>
				</CardContent>
			</Card>
			<ConfirmDialog
				open={confirmDeleteSelection}
				title="Delete Selection"
				description="Are you sure you want to delete this selection of numbers?"
				actionOneText="Confirm"
				actionOneHandler={() => handleConfirmDeleteSelection(selectionToDelete)}
				actionTwoText="Cancel"
				actionTwoHandler={() => setConfirmDeleteSelection(false)} />
			<Dialog open={confirmDownloadEmail}>
				<DialogTitle>Download DNC List</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2}>
						<Typography variant='body2' color='textSecondary' gutterBottom>
							Please enter the email address to receive the DNC list.
						</Typography>
						<TextField
							margin="dense"
							label="Email Address"
							type="email"
							fullWidth
							variant="outlined"
							value={downloadEmail}
							onChange={(e) => setDownloadEmail(e.target.value)}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => {
						setConfirmDownloadEmail(false);
						setDownloadEmail('');
					}}
						sx={actionTwoButtonStyle}
					>
						Cancel
					</Button>
					<Button onClick={downloadDNCList}
						sx={actionOneButtonStyle}
					>
						Download
					</Button>
				</DialogActions>
			</Dialog>
		</Box >
	);
}
